import { FC, useEffect, useMemo, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { LoadingOverlay } from '../components';
import { Status, useAuthContext, useSubscriptionContext } from '../data-access';
import { useFeatureFlag } from '../utils';
import { Path } from './path';
import { isTeamInvitePath, setPendingTeamInviteRedirect } from './teamInviteRedirect';

const TEAM_FALLBACK_PATH = `${Path.HOME}/calendar`;

const TeamInviteEntryRoute: FC = () => {
  const location = useLocation();
  const { status } = useAuthContext();
  const { hasAccessToApp, isValidating } = useSubscriptionContext();
  const { featureFlags, isFeatureFlagsLoading } = useFeatureFlag();
  const [storedInvitePath, setStoredInvitePath] = useState<string | null>(null);
  const currentPath = `${location.pathname}${location.search}`;
  const isInvitePath = useMemo(
    () => isTeamInvitePath(location.pathname, location.search),
    [location.pathname, location.search]
  );

  useEffect(() => {
    if (isInvitePath) {
      setPendingTeamInviteRedirect(location.pathname, location.search);
    }

    setStoredInvitePath(currentPath);
  }, [currentPath, isInvitePath, location.pathname, location.search]);

  if (!isInvitePath) {
    if (isFeatureFlagsLoading) {
      return <LoadingOverlay />;
    }

    return <Navigate to={featureFlags.FF_team_plan ? Path.TEAM : TEAM_FALLBACK_PATH} replace />;
  }

  if (storedInvitePath !== currentPath) {
    return <LoadingOverlay />;
  }

  if (status === Status.LOADING || status === Status.SIGNING_IN) {
    return <LoadingOverlay />;
  }

  if (status === Status.UN_AUTHORIZED) {
    return <Navigate to="/" state={{ redirectTo: currentPath }} replace />;
  }

  if (isValidating) {
    return <LoadingOverlay />;
  }

  if (!hasAccessToApp) {
    return <Navigate to={Path.PAYWALL} replace />;
  }

  return <Outlet />;
};

export default TeamInviteEntryRoute;
